/**
 * useInvestmentData(member) — React hook that loads everything the Home
 * screen needs and reshapes Supabase rows into the same object shape the
 * mock-data prototype expected. This means Home.jsx (and any other screen)
 * can keep referring to fields like data.accountHolder.hurdle without
 * caring whether the values came from a hand-written mock or a real query.
 *
 * Returns:
 *   { status, data, error, reload }
 *
 * status:
 *   'loading'            — initial load in flight
 *   'no-enrollment'      — member is not enrolled in the investment module
 *   'enrolled-no-loop'   — enrolled but no active subscriptions
 *   'enrolled-no-events' — has subscription(s) but no value_events yet
 *   'subscribed'         — has events; full render path
 *   'error'              — see error
 *
 * data is null unless status is one of 'enrolled-*' or 'subscribed'.
 *
 * Block 3 scope: read-only. Mutations (subscribe, add sub-account, settle)
 * are individual screens' responsibility in later blocks.
 */
import { useCallback, useEffect, useState } from 'react';
import { getSupabase } from './supabase.js';
import { buildActivityRows } from './activityRows.js';
import { POOL_ENTRY_TYPES, shapePoolEntry } from './poolEntries.js';
import { settlementRows } from './settlementRows.js';
import { shapeSchedule } from './payoutSchedule.js';

// Deterministic color per member.id (so Mahmoud is always indigo, etc).
const MEMBER_PALETTE = ['#4f46e5', '#0ea5e9', '#f59e0b', '#ec4899', '#10b981', '#8b5cf6', '#ef4444', '#14b8a6', '#a855f7', '#06b6d4'];
function memberColor(id) {
  let h = 0;
  for (let i = 0; i < (id || '').length; i++) h = (h * 31 + id.charCodeAt(i)) >>> 0;
  return MEMBER_PALETTE[h % MEMBER_PALETTE.length];
}
const initial = (name) => (name || '?').trim().charAt(0).toUpperCase() || '?';

// The steps one cycle actually runs through. A loop can hold the same load on
// both Taekus cards (Skrill and Wise each have a TK-B and a TK-C step into the
// same account), but a cycle uses ONE of them — summing both double-counts the
// loop's rate, fee and duration. Count only the higher-rated card step into
// each destination. Accepts shaped (camelCase) or raw (snake_case) steps.
const CARD_KINDS = new Set(['taekus_business', 'taekus_corp']);
export function cycleSteps(steps) {
  const list = Array.isArray(steps) ? steps : [];
  const src = (st) => st?.sourceKind ?? st?.source_kind;
  const dst = (st) => st?.destKind ?? st?.dest_kind;
  const rate = (st) => Number(st?.rate ?? st?.value_rate ?? 0);
  const keep = {};
  list.forEach((st, i) => {
    if (!CARD_KINDS.has(src(st)) || !dst(st)) return;
    const k = dst(st);
    if (keep[k] == null || rate(st) > rate(list[keep[k]])) keep[k] = i;
  });
  return list.filter((st, i) => !CARD_KINDS.has(src(st)) || !dst(st) || keep[dst(st)] === i);
}

// How a loop is DRAWN. Adjacent card loads into the same account (the pairs
// cycleSteps collapses) share one position and render as branches that
// converge on that account, labelled by card: Step 1B (TK-B), Step 1C (TK-C).
// Later steps renumber, so Wise reads 1B · 1C · 2 · 3 · 4. Returns copies of
// the steps carrying { position, branch, label, positions }.
const CARD_LETTER = { taekus_business: 'B', taekus_corp: 'C' };
export function labelLoopSteps(steps) {
  const list = Array.isArray(steps) ? steps : [];
  const src = (st) => st?.sourceKind ?? st?.source_kind;
  const dst = (st) => st?.destKind ?? st?.dest_kind;
  let pos = 0;
  const placed = list.map((st, i) => {
    const prev = list[i - 1];
    const joinsPrev = prev && CARD_KINDS.has(src(st)) && CARD_KINDS.has(src(prev)) && dst(st) && dst(st) === dst(prev);
    if (!joinsPrev) pos += 1;
    return pos;
  });
  const size = {};
  placed.forEach((p) => { size[p] = (size[p] || 0) + 1; });
  return list.map((st, i) => {
    const p = placed[i];
    const branch = size[p] > 1 ? (CARD_LETTER[src(st)] || String.fromCharCode(65 + placed.slice(0, i).filter((q) => q === p).length)) : null;
    return { ...st, position: p, branch, label: branch ? `${p}${branch}` : String(p), positions: pos };
  });
}

// Group labelled steps by position: [{ position, steps: [...] }]. A position
// with more than one step is a branch.
export function loopPositions(steps) {
  const labelled = (steps || []).length && steps[0].position != null ? steps : labelLoopSteps(steps);
  const out = [];
  labelled.forEach((st) => {
    const last = out[out.length - 1];
    if (last && last.position === st.position) last.steps.push(st);
    else out.push({ position: st.position, steps: [st] });
  });
  return out;
}

// Auto-derive a loop's cycle velocity (in calendar days) from its steps.
// 1 business day ≈ 7/5 calendar days. Returns null when no step has any
// duration data (sum 0) — caller treats that as "can't annualize APY".
// Shared across useInvestmentData / useCatalogData / AdminOverview /
// CatalogAdmin so every surface sees the same number.
export function deriveVelocityDays(shapedSteps) {
  if (!Array.isArray(shapedSteps) || shapedSteps.length === 0) return null;
  const total = cycleSteps(shapedSteps).reduce((sum, st) => {
    const sd = Number(st.stepDurationDays || 0);
    const wd = Number(st.waitDurationDays || 0);
    const stepCal = st.stepDurationIsBusiness ? sd * (7 / 5) : sd;
    const waitCal = st.waitDurationIsBusiness ? wd * (7 / 5) : wd;
    return sum + stepCal + waitCal;
  }, 0);
  return total > 0 ? total : null;
}

// Per-loop "at max throughput" economics — what would this loop produce
// if it ran at the maxThroughput cap? Returns null when max, velocity,
// or per-cycle rate isn't set. Shared between the investor Catalog
// (per-loop preview) and the admin Catalog (aggregate hero stats).
//
// Two regimes for the capital-vs-cap interaction (extracted from Catalog
// notes — same math, single source of truth now):
//   • Cycle FASTER than the cap's unit window → capital scales DOWN to
//     land exactly on the cap: capital = max × velocity / unit_days.
//   • Cycle SLOWER than the cap's unit window → capital is bounded BY
//     the cap (extra capital would just sit unused): capital = max.
// Math.min covers both cleanly.
export function maxEconomics(loop) {
  const max = Number(loop?.maxThroughput || 0);
  const perCycle = cycleSteps(loop?.steps).reduce((s, st) => s + Number(st.rate || 0), 0);
  const velocity = Number(loop?.velocityDays || 0);
  if (max <= 0 || velocity <= 0 || perCycle <= 0) return null;
  const unit = loop?.maxThroughputUnit || 'per_cycle';
  const unitDays = unit === 'per_month' ? 30 : unit === 'per_week' ? 7 : velocity;
  const capital = Math.min(max, max * (velocity / unitDays));
  const cyclesPerMonth = 30 / velocity;
  const volumeMonthly = capital * cyclesPerMonth;
  const gross = volumeMonthly * perCycle;
  const cost = Number(loop?.monthlyOperatingCost || 0);
  // D23 (T-022): per-transaction fees scale with volume — each step's fee
  // rate charges on the amount that moves through it, so a cycle's fee load
  // is Σ(step fee rates) × capital, i.e. volume × blended fee rate monthly.
  const perCycleFee = cycleSteps(loop?.steps).reduce((s, st) => s + Number(st.feeRate || 0), 0);
  const fees = volumeMonthly * perCycleFee;
  const net = gross - cost - fees;
  const apy = capital > 0 ? (net / capital) * 12 : 0;
  const limitedBy = velocity > unitDays ? 'cycle' : 'cap';
  return { max, capital, gross, cost, fees, perCycleFee, net, apy, cyclesPerMonth, perCycle, unit, limitedBy, volumeMonthly };
}


// Kinds that are MECHANISM, not the investor's own accounts, and must never
// appear in Balances (owner, 2026-08-08: "Kholy should not have a balance for
// Kareem Trust Wallet, or card purchase, Taekus corp").
//
//   taekus_corp        — Kareem's pool card. The investor draws on it; the
//                        money is not theirs. Their pool standing is the
//                        dedicated pool card, not an account balance.
//   kareem_*           — pool-side conduits. A movement INTO one records a
//                        transfer to Kareem, not a balance the investor holds.
//   pool_carryover /
//   gold_payable       — investor-side conduits for the same reason.
//   card_spend         — a catch-all destination for non-loop card charges,
//                        not somewhere money sits.
const NON_BALANCE_KINDS = new Set([
  'taekus_corp',
  'kareem_trust', 'kareem_kraken', 'kareem_gold_bridge', 'kareem_carryover',
  'pool_carryover', 'gold_payable',
  'invest_payable', 'kareem_invest_bridge',   // D-9 payout-conversion conduits
  'card_spend',
]);

// Make a human "May 13" style date label from an ISO date string.
// Rendered in UTC on purpose. event_date is a timestamptz, and hand-logged
// rows are stamped midnight UTC — which is 8pm the previous evening in EDT, so
// local formatting showed most of the ledger a day early while API-sourced
// rows (which carry a real transaction time) showed the right day. One charge
// could appear on two different dates. The ledger's day must not depend on who
// is looking at it.
export function dateLabel(d) {
  if (!d) return '';
  try {
    return new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', timeZone: 'UTC' });
  } catch { return d.slice(0, 10); }
}

// Compute "next monthly nudge" — first of next month given today.
function nextMonthlyNudge() {
  const d = new Date();
  d.setMonth(d.getMonth() + 1);
  d.setDate(1);
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

// ─── The old meter is dead (D-15 Session C, owner 2026-08-23) ────────────────
// The pro-rata accrual helpers and their cost-history fetches are gone —
// they were the retired auto-accrual's client half: split-share guesses
// with no custody term (the T-034 custody-blind fallback). The summary
// contract is the ONLY source of money figures; when it cannot be read,
// screens show failure, never a plausible wrong number.

export function useInvestmentData(member, profile) {
  const [state, setState] = useState({ status: 'loading', data: null, error: null });
  const memberId = member?.id;
  const isAdmin = profile?.role === 'admin';

  const load = useCallback(async () => {
    setState((s) => ({ ...s, status: 'loading' }));
    const sb = getSupabase();
    if (!sb || !memberId) {
      setState({ status: 'no-enrollment', data: null, error: null });
      return;
    }

    try {
      // 1. Enrollment in the investment module.
      const { data: enrollmentRow } = await sb
        .from('module_enrollment')
        .select('*')
        .eq('member_id', memberId)
        .eq('module_name', 'investment')
        .eq('enabled', true)
        .maybeSingle();

      // Admin always has access to the Home screen. If they don't have a
      // real enrollment row, fall back to a virtual one (no hurdle, default
      // split + cadence) so the page renders instead of showing an empty
      // "not enrolled" state. They can still toggle on a real enrollment
      // with custom config from Settings → Executors → ⚙.
      const enrollment = enrollmentRow || (isAdmin ? {
        member_id: memberId,
        module_name: 'investment',
        enabled: true,
        enrolled_at: new Date().toISOString(),
        config: { kareem_initial_capital: 0, kareem_recovered: 0, split_after_hurdle: 0.5, settlement_cadence: 'monthly' },
        _virtual: true,
      } : null);

      if (!enrollment) {
        setState({ status: 'no-enrollment', data: null, error: null });
        return;
      }

      // 2. Family: the account holder + any sub-accounts they manage.
      //    We fetch the holder row separately (in parallel) so we can pull
      //    their picked `color` from members — previously the holder color
      //    was always a hash of id, so the "edit color" UI had nowhere to
      //    persist to and the bubble couldn't reflect the pick.
      const [{ data: holderMemberRow }, { data: subAccounts }] = await Promise.all([
        sb.from('members')
          .select('id, name, display_name, color, monthly_cost')
          .eq('id', memberId)
          .maybeSingle(),
        sb.from('members')
          .select('id, name, display_name, managed_by_member_id, active, color, monthly_cost')
          .eq('managed_by_member_id', memberId)
          .eq('active', true),
      ]);

      const familyIds = [memberId, ...(subAccounts || []).map((s) => s.id)];

      // 3. Financial accounts for every family member.
      const { data: accountsRaw } = await sb
        .from('financial_accounts')
        .select('id, member_id, account_kind, institution, nickname, active')
        .in('member_id', familyIds)
        .eq('active', true);

      const accountsByMember = {};
      (accountsRaw || []).forEach((a) => {
        (accountsByMember[a.member_id] = accountsByMember[a.member_id] || []).push(a);
      });

      // 3a. Balance anchors (separate, resilient query). These columns
      //     (balance_anchor / balance_anchor_at / balance_set_manually) back
      //     the live-balance feature. We fetch them on their own and tolerate
      //     failure so that, if this deploys before the migration is applied,
      //     the rest of the dashboard still loads — balances just fall back to
      //     a pure auto-tally (anchor 0) until the columns exist.
      const balAnchors = {};
      try {
        const { data: ba, error: baErr } = await sb
          .from('financial_accounts')
          .select('id, balance_anchor, balance_anchor_at, balance_set_manually')
          .in('member_id', familyIds)
          .eq('active', true);
        if (!baErr && ba) ba.forEach((r) => { balAnchors[r.id] = r; });
      } catch { /* columns not present yet — auto-tally fallback */ }

      // 3b. Raw money movements touching any family account — drives the
      //     live balance per account. We need ALL movements (not just the
      //     ones that produced value events) so 0%-rate legs and off-loop
      //     transfers still move the balance. Live balance =
      //       balance_anchor + Σ(in) − Σ(out)  for movements created AFTER
      //     balance_anchor_at. Movements before the anchor are assumed to be
      //     already reflected in the (manually corrected) anchor number.
      const acctIds = (accountsRaw || []).map((a) => a.id);
      let balanceMovements = [];
      if (acctIds.length) {
        const inList = acctIds.join(',');
        const { data: mv } = await sb
          .from('money_movements')
          // movement_time / effective_at: the money's OWN landing time, read by
          // the T-052 hold calculator (created_at is only when it was logged).
          .select('id, source_account_id, dest_account_id, amount, movement_date, movement_time, effective_at, created_at, status, notes')
          .or(`source_account_id.in.(${inList}),dest_account_id.in.(${inList})`);
        balanceMovements = mv || [];
      }

      // 4. Subscriptions (this enrolled member's), their loops, the
      //    step references in those loops, and the steps themselves.
      const { data: subs } = await sb
        .from('loop_subscriptions')
        .select('id, loop_id, active, paused, started_at')
        .eq('enrolled_member_id', memberId)
        .eq('active', true);

      const loopIds = (subs || []).map((s) => s.loop_id);
      // T-020: ACTIVE loops only — deactivating a loop hides it investor-side
      // everywhere downstream (Home cards, log modal) while
      // the subscription row is preserved, so reactivating the loop restores
      // everyone instantly. History is unaffected: value_events/settlements
      // are fetched per member below, not through this list.
      const [loopsRes, refsRes, allStepsRes] = await Promise.all([
        loopIds.length
          ? sb.from('loops').select('*').in('id', loopIds).eq('active', true)
          : Promise.resolve({ data: [] }),
        loopIds.length
          ? sb.from('loop_step_refs').select('*').in('loop_id', loopIds).order('step_order')
          : Promise.resolve({ data: [] }),
        sb.from('steps').select('*'), // need for any step ids referenced in events/refs
      ]);
      const loops = loopsRes.data || [];
      const refs = refsRes.data || [];
      const allSteps = allStepsRes.data || [];
      const stepsById = Object.fromEntries(allSteps.map((s) => [s.id, s]));

      // Skip state. The next-step "Skip" must key off the SAME position anchor
      // the Telegram reminder suppresses on — the shared brain's landed_at
      // (investor_next_steps), not the card's value-event date. So load both the
      // view's landed_at per loop and the member's recorded skips here.
      const [nextStepsRes, skipsRes] = await Promise.all([
        sb.from('investor_next_steps').select('loop_id, landed_at, due_date, days_remaining, next_step_order').eq('member_id', memberId),
        sb.from('next_step_skips').select('loop_id, landed_at').eq('member_id', memberId),
      ]);
      const viewLandedByLoop = {};
      const brainByLoop = {};
      (nextStepsRes.data || []).forEach((r) => {
        viewLandedByLoop[r.loop_id] = r.landed_at || null;
        brainByLoop[r.loop_id] = { dueDate: r.due_date || null, daysRemaining: r.days_remaining, nextStepOrder: r.next_step_order };
      });
      const skipSet = {};
      (skipsRes.data || []).forEach((r) => { skipSet[r.loop_id + '|' + (r.landed_at || '')] = true; });

      // 5. (T-043) The loop_subscription_allocations fetch is retired with
      //    the allocations concept — nothing reads planned per-member $ any
      //    more, and per-loop cost templates died with the D-15 cost
      //    redesign (schedules + items). The table stays in the DB as
      //    historical record only.

      // 6. Value events for this enrolled member's ledger.
      //    (account_owner_id is per-sub-account; we show the whole household
      //    by filtering on enrolled_member_id, which is the parent.)
      const { data: events } = await sb
        .from('value_events')
        .select('*')
        .eq('enrolled_member_id', memberId)
        // Live events only. A voided row is a SUPERSEDED version, not a second
        // event: the engine voids and re-posts a household's whole ledger on
        // every replay (a rate correction, an edited movement, a cost-model
        // change), so the voided pile grows without anything being duplicated.
        // Kholy sat at 47 live / 131 voided — the feed was showing all 178 and
        // reading as if every charge had happened four times.
        .is('voided_at', null)
        .order('event_date', { ascending: false });

      // 6a. The accounts each event's movement ACTUALLY used.
      //     The flow label used to be read off the matched step, which is a
      //     classification, not a record of what happened — so a movement
      //     could display a route it never took. Kholy's cash-outs read
      //     "Coinbase → SoFi" (movement) or "Coinbase → Kraken" (step) while
      //     the money went to Mercury. Three sources, three answers.
      //     The movement's own accounts are the record; the step stays as a
      //     fallback for events with no movement behind them.
      const evMovementIds = [...new Set((events || []).map((e) => e.movement_id).filter(Boolean))];
      const mvAccounts = {};   // movement_id → { source_account_id, dest_account_id }
      const kindOfAccount = {};
      (accountsRaw || []).forEach((a) => { kindOfAccount[a.id] = a.account_kind; });
      if (evMovementIds.length) {
        const { data: evMv } = await sb
          .from('money_movements')
          .select('id, source_account_id, dest_account_id')
          .in('id', evMovementIds);
        (evMv || []).forEach((m) => { mvAccounts[m.id] = m; });
        // Conduit accounts (carryover, gold bridge) are provisioned inactive
        // and Kareem-side accounts are not in the family list, so resolve
        // whatever the first pass missed rather than falling back for them.
        const missing = [...new Set(Object.values(mvAccounts)
          .flatMap((m) => [m.source_account_id, m.dest_account_id])
          .filter((id) => id && !kindOfAccount[id]))];
        if (missing.length) {
          const { data: extra } = await sb
            .from('financial_accounts').select('id, account_kind').in('id', missing);
          (extra || []).forEach((a) => { kindOfAccount[a.id] = a.account_kind; });
        }
      }
      // The two account kinds an event moved between — the movement's own
      // accounts when it has one, the step's classification otherwise. The
      // Activity filters (origin / destination) key off these slugs, so they
      // are exposed on each event alongside the rendered flow label.
      const flowKinds = (e) => {
        const mv = mvAccounts[e.movement_id];
        const src = mv && kindOfAccount[mv.source_account_id];
        const dst = mv && kindOfAccount[mv.dest_account_id];
        if (src && dst) return { src, dst };
        const st = stepsById[e.step_id];
        return { src: st?.source_kind || null, dst: st?.dest_kind || null };
      };
      const flowLabel = (e) => {
        const { src, dst } = flowKinds(e);
        return kindLabel(src) + ' → ' + kindLabel(dst);
      };
      const flowFields = ({ src, dst }) => ({
        srcKind: src, dstKind: dst,
        srcLabel: src ? kindLabel(src) : '', dstLabel: dst ? kindLabel(dst) : '',
      });

      // 7. Settlements for this enrolled member.
      const { data: settlements } = await sb
        .from('investment_settlements')
        .select('*')
        .eq('enrolled_member_id', memberId)
        .order('settle_date', { ascending: false });

      // 7b. Authoritative household summary — Release 5 (T-022). The SQL
      //     contract (v2_household_summary) is the ONE place the money math
      //     lives: D4 waterfall over confirmed facts (D19 attestations + D23
      //     per-transaction fees), D21 pool-custody term, kind-aware payments
      //     vs waivers (D18). Every surface reads it so figures can't drift.
      //     Tolerant: null on any failure → the legacy client math below
      //     still renders the module against an older database.
      //     NOT tolerant any more. It used to fall back to client-side math on
      //     any failure, and that math has no custody term — it sums every
      //     kareem_share regardless of who holds the value. For Khaled that
      //     silently reads 1,809.63 instead of 677.55, overstating what he owes
      //     by the whole pool-held amount, with no error and nothing on screen
      //     to distinguish it. The gap grows with pool activity, so it is
      //     largest for exactly the investors who draw most on Kareem's card.
      //     A wrong number that looks right is worse than a visible failure.
      let summary = null;
      const { data: sumRow, error: sumErr } = await sb.rpc('v2_household_summary', { p_member_id: memberId });
      if (sumErr || !sumRow || typeof sumRow !== 'object' || !sumRow.holder) {
        throw new Error(
          'Could not load the household summary, so the money figures cannot be shown. '
          + (sumErr?.message || 'v2_household_summary returned nothing usable.'));
      }
      summary = sumRow;

      // 7c. Payout schedule (owner, 2026-10-19): each month's balance is paid
      //     by the end of the next month. Same waterfall as the summary, cut
      //     at month ends. Tolerant: it only adds a "when", never changes a
      //     figure, so a failure hides the schedule instead of the screen.
      let schedule = null;
      try {
        const { data: schRow, error: schErr } = await sb.rpc('v2_settlement_schedule', { p_member_id: memberId });
        if (schErr) console.warn('[useInvestmentData] payout schedule unavailable', schErr.message);
        else if (schRow && typeof schRow === 'object' && schRow.holder) schedule = schRow;
      } catch (e) { console.warn('[useInvestmentData] payout schedule unavailable', e); }

      // 8. Catalog count (how many loops exist total — for the "X in catalog" stat).
      const { count: catalogCount } = await sb
        .from('loops')
        .select('id', { count: 'exact', head: true })
        .eq('active', true);

      // 9. Account-kind taxonomy → friendly labels.
      //    The parent dashboard maintains inv_account_kinds (slug → label).
      //    Without this, the React island falls back to humanized slugs
      //    ("taekus_business" → "taekus business") which look like noise
      //    next to the institution name in step cards.
      const { data: kindsRaw } = await sb
        .from('inv_account_kinds')
        .select('name, label, logo_url');
      const kindMetaBySlug = Object.fromEntries((kindsRaw || []).map((k) => [k.name, k]));
      const kindLabel = (slug) => {
        if (!slug) return '';
        return kindMetaBySlug[slug]?.label || slug.replace(/_/g, ' ');
      };
      const kindLogo = (slug) => kindMetaBySlug[slug]?.logo_url || null;

      // Brand-logo lookup with an institution-name fallback. Some legacy
      // accounts carry a generic account_kind (e.g. crypto_exchange, savings_hy)
      // that isn't in the taxonomy, so kindLogo(slug) is null and the row falls
      // back to an initials tile. Match the account's institution against a
      // kind's label/name to still surface the brand icon — exact match first,
      // then a prefix either way so "Taekus" → "Taekus Business" logo.
      const kindsArr = kindsRaw || [];
      const normBrand = (s) => String(s || '').toLowerCase().replace(/[^a-z0-9]/g, '');
      const logoForInstitution = (institution) => {
        const key = normBrand(institution);
        if (!key) return null;
        let hit = kindsArr.find((k) => normBrand(k.label) === key || normBrand(k.name) === key);
        if (!hit) hit = kindsArr.find((k) => {
          const lk = normBrand(k.label);
          return lk && (lk.startsWith(key) || key.startsWith(lk));
        });
        return hit?.logo_url || null;
      };
      const logoFor = (slug, institution) => kindLogo(slug) || logoForInstitution(institution);

      // ── Reshape ────────────────────────────────────────────────────────────
      const cfg = enrollment.config || {};
      const hurdle = Number(cfg.kareem_initial_capital ?? 0);
      // `kareem_recovered` in config tracks the GROSS tally written by the
      // process_value_event trigger. The display-facing "recovered" we
      // surface below is the NET view — gross minus Kareem's split-share
      // of accrued operating cost. This keeps hurdle progress consistent
      // with settled + outstanding (which is also net), and matches the
      // trigger's own hurdle-vs-split decision rule (see
      // migrations/2026-05-24-hurdle-on-net.sql).
      const recoveredGross   = Number(cfg.kareem_recovered ?? 0);
      const splitAfterHurdle = Number(cfg.split_after_hurdle ?? 0.5);
      const settlementCadence = cfg.settlement_cadence || 'monthly';

      const holderRow = {
        id: memberId,
        name: member.display_name || member.name || 'You',
        // Prefer the user-picked color from members.color; the read-side
        // mappings fall back to the deterministic hash when this is null.
        color: holderMemberRow?.color || null,
      };

      const fmtAcct = (a) => `${a.institution} · ${(a.account_kind || '').replace(/_/g, ' ')}`;

      const familyMembers = [
        {
          id: holderRow.id,
          name: holderRow.name,
          initials: initial(holderRow.name),
          role: 'holder',
          color: holderRow.color || memberColor(holderRow.id),
          monthlyCost: Number(holderMemberRow?.monthly_cost || 0),
          valueYTD: 0,        // filled below
          moneyFlow: 0,       // filled below
          accounts: (accountsByMember[holderRow.id] || []).map(fmtAcct),
        },
        ...(subAccounts || []).map((s) => ({
          id: s.id,
          name: s.display_name || s.name,
          initials: initial(s.display_name || s.name),
          role: 'sub',
          // Prefer the user-picked color from members.color; fall back to the
          // deterministic hash for legacy rows that pre-date the column.
          color: s.color || memberColor(s.id),
          monthlyCost: Number(s.monthly_cost || 0),
          valueYTD: 0,
          moneyFlow: 0,
          accounts: (accountsByMember[s.id] || []).map(fmtAcct),
        })),
      ];

      // Aggregate value events per member (account_owner_id) and per step.
      const valuesByMember = {};
      const flowByMember = {};
      const volumeByMember = {};
      const eventsByStep = {};
      (events || []).forEach((e) => {
        const owner = e.account_owner_id || holderRow.id;
        valuesByMember[owner] = (valuesByMember[owner] || 0) + Number(e.value_generated || 0);
        // "Money cycled" = capital PUT TO WORK, not the sum of every leg.
        //
        // A loop carries its whole rate on step 1 and returns the money home
        // at 0% (verified: every active loop has exactly one earning step, or
        // none). So summing all legs counts the same dollar up to four times
        // as it travels round — Kholy's 149,000 of capital showed as 407,015,
        // and sitting beside "Value generated" it invited the obvious ratio:
        // 3,278 / 407,015 = 0.8%, when the real rate is 3,278 / 149,000 =
        // 2.2% exactly. Counting only the earning legs makes the two hero
        // numbers divide into the actual rate.
        //
        // Total volume across every leg is kept as moneyVolume for anyone who
        // wants it; it is just not what belongs next to value generated.
        if (Number(e.value_rate_snapshot || 0) > 0) {
          flowByMember[owner] = (flowByMember[owner] || 0) + Number(e.amount_moved || 0);
        }
        volumeByMember[owner] = (volumeByMember[owner] || 0) + Number(e.amount_moved || 0);
        (eventsByStep[e.step_id] = eventsByStep[e.step_id] || []).push(e);
      });
      familyMembers.forEach((m) => {
        m.valueYTD = Math.round(valuesByMember[m.id] || 0);
        m.moneyFlow = Math.round(flowByMember[m.id] || 0);
        m.moneyVolume = Math.round(volumeByMember[m.id] || 0);
      });
      // HOUSEHOLD totals. The hero used to read these off DATA.loop, which is
      // loops[0] — a legacy single-loop alias — so with more than one active
      // loop it silently reported a fraction. Kholy's hurdle ring (household,
      // 3,278) disagreed with his "Value generated · YTD" (loops[0], 1,760)
      // for exactly that reason.
      const householdValueYTD = Math.round(
        (events || []).reduce((s, e) => s + Number(e.value_generated || 0), 0));

      // Build one loop entry per active subscription.
      // Only CONFIRMED settlements reduce what's owed. Pending submissions
      // still need Kareem's confirmation before they pay down outstanding.
      const totalKareemShare = (events || []).reduce((s, e) => s + Number(e.kareem_share || 0), 0);
      const isConfirmed     = (r) => (r.status || 'pending') === 'confirmed';
      const totalSettled    = (settlements || []).filter(isConfirmed).reduce((s, r) => s + Number(r.amount || 0), 0);
      const totalPending    = (settlements || []).filter((r) => !isConfirmed(r)).reduce((s, r) => s + Number(r.amount || 0), 0);
      // Net settlement math (see helper module/notes below): we split
      // operating costs by the same ratio as profit, so Kareem absorbs
      // his fair share of what the household paid to run the loops.
      // Equivalent to: split × (gross − cost) instead of split × gross.
      // Accrued cost = loop-attached costs + per-member NON-LOOP costs. Both
      // accrue append-only (no retro on rate change) and are split the same
      // way, so Kareem absorbs his share of every operating cost the household
      // carries, loop-specific or not.
      // Release 5 (T-022): the summary contract IS the number. There is no
      // longer a client-side fallback — see the throw above for why.
      const S = {
        outstanding:      Number(summary.outstanding || 0),
        credit:           Number(summary.credit || 0),
        claim:            Number(summary.claim || 0),
        profitPool:       Number(summary.profitPool || 0),
        totalValue:       Number(summary.totalValue || 0),
        recognizedCosts:  Number(summary.recognizedCosts || 0),
        attestedCosts:    Number(summary.attestedCosts || 0),   // D-15: retired key, always 0
        itemCosts:        Number(summary.itemCosts || 0),
        transactionFees:  Number(summary.transactionFees || 0),
        // D-13: who paid decides the effect. kareem-paid costs slow the
        // profit clock but never reduce the claim, so the claim-side cost
        // leg (what relieves THEIR bill) is recognized − kareem-paid.
        kareemPaidCosts:  Number(summary.kareemPaidCosts || 0),
        claimCosts:       Number(summary.recognizedCosts || 0) - Number(summary.kareemPaidCosts || 0),
        poolHeldValue:    Number(summary.poolHeldValue || 0),
        waivers:          Number(summary.waivers || 0),
        confirmedPayments: Number(summary.confirmedPayments || 0),
        // D-9: cash split by direction. confirmedPayments is the NET (in −
        // out) — the waterfall term — and goes negative once payouts exceed
        // collections, so display figures must use these instead. Fallback
        // (pre-D-9 contract): everything confirmed was inbound.
        paidByInvestor:   Number(summary.paidByInvestor ?? summary.confirmedPayments ?? 0),
        paidToInvestor:   Number(summary.paidToInvestor ?? 0),
        pendingPayments:  Number(summary.pendingPayments || 0),
        hurdleProgress:   Number(summary.hurdleProgress || 0),
        // The investor's side of the split, all-time: rewards − the costs
        // that relieve their bill − Kareem's claim. 0 below the hurdle.
        investorNet:      Number(summary.investorNet || 0),
        costsProvisional: summary.costsProvisional !== false,
      };
      const outstanding = S.outstanding;
      const recovered   = S.hurdleProgress;

      const firstInst = (kind) => {
        const a = (accountsByMember[holderRow.id] || []).find((x) => x.account_kind === kind);
        return a ? a.institution : null;
      };

      // Shape every subscription as a loop entry. Each loop carries its
      // own steps with per-step events, executed counts, and valueYTD.
      // (T-043: the per-member allocation/cost maps are retired.)
      const loopShapes = (subs || []).map((sub) => {
        const loopRow = loops.find((l) => l.id === sub.loop_id);
        if (!loopRow) return null;
        const myRefs = refs.filter((r) => r.loop_id === sub.loop_id).sort((a, b) => a.step_order - b.step_order);
        const lastStepId = myRefs.length ? myRefs[myRefs.length - 1].step_id : null;
        const firstStepId = myRefs.length ? myRefs[0].step_id : null;
        const loopValueYTD = myRefs.reduce((s, r) => s + (eventsByStep[r.step_id] || []).reduce((a, e) => a + Number(e.value_generated || 0), 0), 0);
        const loopCyclesCompleted = lastStepId ? (eventsByStep[lastStepId] || []).length : 0;
        const loopCyclesInProgress = firstStepId ? Math.max(0, (eventsByStep[firstStepId] || []).length - loopCyclesCompleted) : 0;
        // Find the most-recent value event landed on any step in THIS loop.
        // Drives the per-loop countdown timer + the "Next steps" aggregator
        // on Home — both want to know when the last movement happened and
        // which step it was so they can derive the next-step due date.
        let lastEventAt = null;        // ISO date string of the most-recent event
        let lastEventStepRefIdx = null; // 1-based step_order in myRefs
        myRefs.forEach((r) => {
          (eventsByStep[r.step_id] || []).forEach((e) => {
            const d = e.event_date;
            if (!d) return;
            if (!lastEventAt || d > lastEventAt) {
              lastEventAt = d;
              lastEventStepRefIdx = r.step_order;
            }
          });
        });
        const shapedSteps = myRefs.map((r) => {
          const step = stepsById[r.step_id] || {};
          const stepEvents = eventsByStep[r.step_id] || [];
          const executed = {};
          stepEvents.forEach((e) => {
            const owner = e.account_owner_id || holderRow.id;
            executed[owner] = (executed[owner] || 0) + 1;
          });
          const srcLabel = kindLabel(step.source_kind);
          const dstLabel = kindLabel(step.dest_kind);
          const srcInst = firstInst(step.source_kind);
          const dstInst = firstInst(step.dest_kind);
          return {
            id: step.id,
            idx: r.step_order,
            source: srcLabel,
            sourceKind: step.source_kind || '',
            sourceInstitution: srcInst && srcInst.toLowerCase() !== srcLabel.toLowerCase() ? srcInst : null,
            sourceLogo: kindLogo(step.source_kind),
            dest: dstLabel,
            destKind: step.dest_kind || '',
            destInstitution: dstInst && dstInst.toLowerCase() !== dstLabel.toLowerCase() ? dstInst : null,
            destLogo: kindLogo(step.dest_kind),
            rate: Number(step.value_rate || 0),
            feeRate: step.fee_rate == null ? 0 : Number(step.fee_rate), // D23 per-txn fee (fraction)
            amount: step.amount == null ? null : Number(step.amount),
            stepDurationDays: Number(step.step_duration_days || 0),
            stepDurationIsBusiness: !!step.step_duration_is_business,
            waitDurationDays: Number(step.wait_duration_days || 0),
            waitDurationIsBusiness: !!step.wait_duration_is_business,
            movesYTD: stepEvents.length,
            valueYTD: Math.round(stepEvents.reduce((s, e) => s + Number(e.value_generated || 0), 0)),
            executed,
          };
        });
        return {
          id: loopRow.id,
          subscriptionId: sub.id,
          paused: !!sub.paused,
          name: loopRow.name,
          description: loopRow.description || '',
          // Skip state (view-anchored — matches the agent's reminder suppression).
          viewLandedAt: viewLandedByLoop[loopRow.id] || null,
          skipped: !!skipSet[loopRow.id + '|' + (viewLandedByLoop[loopRow.id] || '')],
          // The shared brain's authoritative next-step due-date (wait_duration off
          // the MOVEMENT date, weekend/holiday-aware). The Home countdown uses
          // this so the card matches the agent's reminders exactly instead of
          // recomputing (step+wait off the event date) and drifting ~1 day.
          brainDueDate: (brainByLoop[loopRow.id] && brainByLoop[loopRow.id].dueDate) || null,
          brainDaysRemaining: brainByLoop[loopRow.id] ? brainByLoop[loopRow.id].daysRemaining : null,
          brainNextStepOrder: brainByLoop[loopRow.id] ? brainByLoop[loopRow.id].nextStepOrder : null,
          // Admin-editable facts (2026-05-21-loop-facts.sql + 2026-05-22 multi
          // payment_methods + 2026-05-23 monthly_operating_cost).
          // paymentMethods is always an array — possibly empty.
          heroLogoUrl:    loopRow.hero_logo_url || null,
          maxThroughput:  loopRow.max_throughput == null ? null : Number(loopRow.max_throughput),
          // Unit for max_throughput. Drives the capital_required + APY math
          // in maxEconomics() on the catalog. Defaults to 'per_cycle' to
          // preserve existing behavior when admins haven't set it yet.
          maxThroughputUnit: loopRow.max_throughput_unit || 'per_cycle',
          // velocity_days is now auto-derived from the steps the same way
          // blendedRate is — sum of (step_duration + wait_duration) across
          // every step in the loop, with biz-days converted to cal-days at
          // 5 biz = 7 cal. The loops.velocity_days column is left in the
          // schema but no longer read or written. Returns null when no
          // step has a duration set (can't annualize APY in that case).
          velocityDays:   deriveVelocityDays(shapedSteps),
          rateOfReturn:   loopRow.rate_of_return == null ? null : Number(loopRow.rate_of_return),
          devicesNeeded:  loopRow.devices_needed == null ? null : Number(loopRow.devices_needed),
          paymentMethods: Array.isArray(loopRow.payment_methods) ? loopRow.payment_methods : [],
          // Loop-level operating cost — fixed at max throughput, independent
          // of any single member. Catalog max-throughput economics use this.
          monthlyOperatingCost: Number(loopRow.monthly_operating_cost || 0),
          valueYTD: Math.round(loopValueYTD),
          cyclesCompleted: loopCyclesCompleted,
          cyclesInProgress: loopCyclesInProgress,
          blendedRate: cycleSteps(shapedSteps).reduce((s, st) => s + Number(st.rate || 0), 0),
          steps: labelLoopSteps(shapedSteps),
          // Most-recent value event landed on any step in this loop, plus
          // the 1-based step_order it was on. Powers per-loop countdown +
          // the Next-steps aggregator on Home. Null when the loop has no
          // events yet (cycle 1 hasn't kicked off).
          lastEventAt,
          lastEventStepIdx: lastEventStepRefIdx,
        };
      }).filter(Boolean);

      const primaryLoop = loopShapes[0] || null;

      // Settlements shape. Contract-first (Release 5 / T-022): the waterfall
      // components ride along so the settle UI can show the calculation —
      // value − costs → pool → claim − pool-held − payments − waivers.
      // Legacy keys (grossKareemShare/costShare) stay populated for the
      // fallback path and map to their nearest waterfall analog otherwise.
      const settlementsShape = {
        // T-039c: unrounded contract terms for the on-screen equations —
        // rounded components don't sum ($9,722−$1,364−$5,917−$2,606 = −$165
        // while the true credit is $163.95→$164), so equations render cents
        // from these while the tiles keep the rounded figures.
        raw: {
          totalValue: S.totalValue,
          recognizedCosts: S.recognizedCosts,
          // D-13: the equation's cost leg is the claim-relieving slice only
          // (investor-paid + fees) — kareem-paid costs never reduce the claim.
          claimCosts: S.claimCosts,
          kareemPaidCosts: S.kareemPaidCosts,
          poolHeld: S.poolHeldValue,
          // D-9: `paid` is what the investor paid IN (the equation's "− paid"
          // term); `paidOut` is Kareem's payouts (rendered "+ paid back").
          // The net lives in the contract; equations render the directions.
          paid: S.paidByInvestor,
          paidOut: S.paidToInvestor,
          waivers: S.waivers,
          investorNet: S.investorNet,
          outstanding: S.outstanding,
          credit: S.credit,
        },
        investorNet: Math.round(S.investorNet),
        schedule: shapeSchedule(schedule),
        outstanding: Math.round(outstanding),
        credit: Math.round(S.credit),
        contract: true,
        claim: Math.round(S.claim),
        profitPool: S.profitPool,
        totalValue: Math.round(S.totalValue),
        recognizedCosts: Math.round(S.recognizedCosts),
        attestedCosts: Math.round(S.attestedCosts),
        transactionFees: Math.round(S.transactionFees),
        kareemPaidCosts: Math.round(S.kareemPaidCosts),
        poolHeld: Math.round(S.poolHeldValue),
        waivers: Math.round(S.waivers),
        costsProvisional: S.costsProvisional,
        grossKareemShare: Math.round(S.claim),
        costShare: 0,
        accruedHouseholdCost: Math.round(S.recognizedCosts),
        split: splitAfterHurdle,
        // D-9: lifetime settled is what the investor paid IN — the net
        // (confirmedPayments) drops below zero once Kareem's payouts exceed
        // collections, and "confirmed lifetime $-300" is nonsense.
        lifetimeSettled: Math.round(S.paidByInvestor),
        paidOutLifetime: Math.round(S.paidToInvestor),
        pendingTotal: Math.round(S.pendingPayments),
        cadence: settlementCadence,
        nextNudge: nextMonthlyNudge(),
        recent: (settlements || []).slice(0, 10).map((r) => ({
          id: r.id,
          dateLabel: dateLabel(r.settle_date),
          amount: Number(r.amount || 0),
          method: (r.payment_method || 'other').toLowerCase(),
          methodName: r.payment_method || 'Other',
          status: r.status || 'pending',
          // D-9 + T-039: direction and kind must survive the map — payout
          // rows render "Kareem → you" and never count as the investor's own
          // payments.
          direction: r.direction || 'to_kareem',
          kind: r.kind || 'cash',
          memo: r.memo || '',
        })),
        demoRecent: [], // intentionally empty — no fake data in real mode
      };

      // Shape EVERY event once; recentEvents is just the first 10 (Home feed),
      // allEvents is the full ledger (the Activity screen renders this).
      // The shaping lives in activityRows.js and is shared with the admin's
      // cross-household Activity screen, so a movement reads identically on
      // both. Value-less movements (rate-less / draft steps, pending rows)
      // are included so the page's own promise — "every movement" — holds;
      // in this household view they belong to the holder.
      const householdAccountIds = new Set((accountsRaw || []).map((a) => a.id));
      const allEvents = buildActivityRows({
        events: events || [],
        movements: (balanceMovements || []).filter((mv) =>
          householdAccountIds.has(mv.source_account_id) || householdAccountIds.has(mv.dest_account_id)),
        mvAccounts,
        ctx: {
          kindOfAccount, stepsById, kindLabel, dateLabel,
          memberOf: () => holderRow.id,
          householdOf: () => memberId,
        },
      });

      // Pool entries — gold payouts, investment payouts, carryover lines —
      // run through INACTIVE conduit accounts, so the active-account movement
      // fetch above never sees them and they were missing from Home and
      // Activity (2026-10-21). v2_pool_activity is the RPC Balances already
      // reads; it is household-scoped server-side for investors. Contributions
      // and TK-C draws are already rows above, so only the three conduit
      // types are added. Tolerant: a failure just leaves them out.
      try {
        const { data: pa, error: paErr } = await sb.rpc('v2_pool_activity', { p_limit: 5000 });
        if (!paErr && Array.isArray(pa)) {
          pa.filter((e) => e.household === memberId && POOL_ENTRY_TYPES.includes(e.entry_type))
            .forEach((e) => allEvents.push(shapePoolEntry(e, { dateLabel, holderId: holderRow.id })));
          allEvents.sort((a, b) => String(b.eventDate || '').localeCompare(String(a.eventDate || '')));
        }
      } catch { /* pool entries stay out; the rest of the ledger still loads */ }

      // Cash settlements (2026-10-23): payments to Kareem, Kareem's cash-out
      // payouts and waivers have no movement behind them, so the ledger
      // never listed them. They are loaded in step 7 already.
      settlementRows(settlements, { dateLabel, holderId: holderRow.id })
        .forEach((row) => allEvents.push(row));
      allEvents.sort((a, b) => String(b.eventDate || '').localeCompare(String(a.eventDate || '')));

      const recentEvents = allEvents.slice(0, 10);

      // ── Live balances per account ────────────────────────────────────────
      // A balance is only meaningful once the investor has SET it: an account's
      // movement history nets to flow (a pass-through loop nets ~0, a funding
      // account goes negative), which is NOT "how much you hold". So we treat a
      // balance as "not set" until corrected once (balance_set_manually). After
      // that, live balance = anchor + net of movements logged AFTER the anchor,
      // so day-to-day moves keep it current without re-typing.
      const memberById = Object.fromEntries(familyMembers.map((m) => [m.id, m]));
      const balances = (accountsRaw || [])
        .filter((a) => !NON_BALANCE_KINDS.has(a.account_kind))
        .map((a) => {
        const ba = balAnchors[a.id] || {};
        const isSet = !!ba.balance_set_manually;
        const anchor = Number(ba.balance_anchor || 0);
        const anchorMs = ba.balance_anchor_at ? new Date(ba.balance_anchor_at).getTime() : 0;
        let delta = 0;
        let pendingDelta = 0;
        balanceMovements.forEach((m) => {
          // A denied movement did not happen: retired duplicates, superseded
          // carryover snapshots and reversed bridges must not move a balance.
          // (Kholy's Mercury balance carried a phantom 10,000 from a duplicate
          // that had already been retired.) NULL status means legacy-confirmed.
          if (m.status === 'denied') return;
          const t = m.created_at ? new Date(m.created_at).getTime() : 0;
          if (t <= anchorMs) return;
          const signed = (m.dest_account_id === a.id ? 1 : m.source_account_id === a.id ? -1 : 0)
            * Number(m.amount || 0);
          if (!signed) return;
          // A PENDING draw has not happened yet either: confirming is the only
          // trigger (D22), so it must not move the balance before a human says
          // so. Tracked separately rather than dropped, so in-flight money is
          // visible instead of silently missing (owner, 2026-08-08).
          if (m.status === 'pending') { pendingDelta += signed; return; }
          delta += signed;
        });
        const owner = memberById[a.member_id];
        return {
          accountId: a.id,
          memberId: a.member_id,
          memberName: owner?.name || '',
          memberColor: owner?.color || memberColor(a.member_id),
          institution: a.institution,
          accountKind: a.account_kind,
          kindLabel: kindLabel(a.account_kind),
          kindLogo: logoFor(a.account_kind, a.institution),
          nickname: a.nickname || null,
          isSet,
          // null until set, so the UI can prompt "Set balance" instead of
          // showing a misleading flow-net number.
          balance: isSet ? Math.round(anchor + delta) : null,
          // Signed net of movements still awaiting confirmation. 0 when none.
          pendingDelta: Math.round(pendingDelta),
          manual: isSet,
          updatedAt: ba.balance_anchor_at || null,
          updatedAtLabel: dateLabel(ba.balance_anchor_at),
        };
      });
      const balanceTotal = balances.reduce((s, b) => s + (b.isSet ? b.balance : 0), 0);
      const balancesSetCount = balances.filter((b) => b.isSet).length;

      // ── Every active household account, for the log-movement pickers ────
      // T-047: a movement's two sides may belong to DIFFERENT household
      // members (Beko's Taekus Business → Soha's Coinbase). The modal needs to
      // know which (member, kind) accounts actually exist — and which are real
      // (balance set, or any live movement history) versus a never-used
      // auto-created row — to default each side to the account the money
      // actually left or landed in. Unlike `balances`, this list keeps the
      // conduit kinds (taekus_corp, kareem_*): they are still legs of a step.
      const historyByAccount = {};
      balanceMovements.forEach((m) => {
        if (m.status === 'denied') return;
        if (m.source_account_id) historyByAccount[m.source_account_id] = (historyByAccount[m.source_account_id] || 0) + 1;
        if (m.dest_account_id) historyByAccount[m.dest_account_id] = (historyByAccount[m.dest_account_id] || 0) + 1;
      });
      const balanceByAccount = Object.fromEntries(balances.map((b) => [b.accountId, b]));
      const accounts = (accountsRaw || []).map((a) => {
        const b = balanceByAccount[a.id];
        const isSet = !!(balAnchors[a.id] || {}).balance_set_manually;
        return {
          id: a.id,
          memberId: a.member_id,
          memberName: memberById[a.member_id]?.name || '',
          accountKind: a.account_kind,
          institution: a.institution,
          isSet,
          balance: b ? b.balance : null,
          movementCount: historyByAccount[a.id] || 0,
          // "Real" = the investor has vouched for it (set its balance) or money
          // has moved through it. A row with neither is an untouched
          // auto-created placeholder the picker should not default to.
          hasHistory: isSet || (historyByAccount[a.id] || 0) > 0,
        };
      });

      const data = {
        accountHolder: {
          id: holderRow.id,
          name: holderRow.name,
          initials: initial(holderRow.name),
          color: holderRow.color || memberColor(holderRow.id),
          enrolledAt: enrollment.enrolled_at,
          enrolledAtLabel: dateLabel(enrollment.enrolled_at),
          hurdle,
          recovered: Math.round(recovered),
          splitAfterHurdle,
          settlementCadence,
        },
        members: familyMembers,
        // The full list of active subscriptions, each shaped as a loop with
        // its own steps + cycle counts. Empty when the holder
        // isn't subscribed to anything.
        loops: loopShapes,
        loop: primaryLoop, // legacy single-loop alias (= loops[0])
        // Household-wide, across every active loop. Prefer these in any
        // whole-household display; DATA.loop.* is one loop's slice.
        householdValueYTD,
        householdCyclesCompleted: loopShapes.reduce((s, l) => s + Number(l.cyclesCompleted || 0), 0),
        // T-032: a loop is active FOR A MEMBER when the loop itself is active
        // and it is assigned to them — nothing else (owner, 2026-08-09: "if a
        // loop is active and assigned to someone it should be active for them.
        // if a loop is entirely inactive, then it should be inactive for
        // everyone"). `subs` is only the assignment side, so counting it read
        // the deactivated easyBitcoin loop as active for Khaled and Kareem —
        // and disagreed with `catalogCount` beside it, which does filter on
        // loops.active. `loops` is already the intersection (fetched with
        // .eq('active', true) at :333), so it IS the answer, and the hero can
        // no longer contradict the cards below it.
        activeLoopsCount: loops.length,
        settlements: settlementsShape,
        recentEvents,
        allEvents,
        balances,
        balanceTotal,
        balancesSetCount,
        // T-052: the raw rows behind the balances, so the Balances screen can
        // run the 48h hold calculator against a live clock (the box has to
        // jump when a timer ends, not when the page next reloads).
        balanceMovements,
        accounts,
        nudges: [],
        catalogCount: catalogCount || 0,
      };

      // Decide status flag.
      let status;
      if (!(subs || []).length) status = 'enrolled-no-loop';
      else if ((events || []).length === 0) status = 'enrolled-no-events';
      else status = 'subscribed';

      setState({ status, data, error: null });
    } catch (err) {
      console.error('[useInvestmentData] load failed', err);
      setState({ status: 'error', data: null, error: err });
    }
  }, [memberId, isAdmin]);

  useEffect(() => { load(); }, [load]);

  // Reload when the parent app dispatches an enrollment change for this
  // member (e.g., admin toggles their own investment on/off in Settings).
  useEffect(() => {
    const onChange = (e) => {
      if (!e?.detail) { load(); return; }
      if (e.detail.memberId === memberId || e.detail.memberId == null) load();
    };
    if (typeof window !== 'undefined') {
      window.addEventListener('finops:enrollment-changed', onChange);
      return () => window.removeEventListener('finops:enrollment-changed', onChange);
    }
  }, [load, memberId]);

  // Skip / un-skip a loop's current next step (batching). Writes next_step_skips
  // keyed to the shared brain's landed_at so it matches the agent's reminder
  // suppression exactly. Then reloads.
  const skipNextStep = useCallback(async (loopId, landedAt) => {
    const sb = getSupabase();
    if (!sb || !memberId) return;
    // Plain insert; a unique-violation (23505) just means it's already skipped,
    // which is the desired end state — ignore it.
    const { error } = await sb.from('next_step_skips')
      .insert({ member_id: memberId, loop_id: loopId, landed_at: landedAt || null });
    if (error && error.code !== '23505') { console.warn('[skip] failed', error.message); }
    await load();
  }, [memberId, load]);

  const unskipNextStep = useCallback(async (loopId, landedAt) => {
    const sb = getSupabase();
    if (!sb || !memberId) return;
    let q = sb.from('next_step_skips').delete().eq('member_id', memberId).eq('loop_id', loopId);
    q = (landedAt == null) ? q.is('landed_at', null) : q.eq('landed_at', landedAt);
    await q;
    await load();
  }, [memberId, load]);

  return { ...state, reload: load, skipNextStep, unskipNextStep };
}
